import { Option } from "components/SingleSelect/types";
import { addToast } from "components/ToastContainer/ToastStore";
import { routeConstants } from "constants/routeConstants";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "slice";
import { useGetCountriesQuery } from "slice/common";
import {
    useGetProfileStrengthQuery,
    useGetUserQuery,
    useSetContactDetailsMutation,
    useUpdateAgencyProfileDataMutation,
} from "slice/user";
import { validations } from "utils/validations";
import getInputErrors from "./getInputErrors";
import { Inputs } from "./types";

export const useContactInfo = () => {
    const [inputs, setInputs] = useState<Inputs>({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        country: "",
        zip: "",
    });

    const [inputErrors, setInputErrors] = useState<Inputs>({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        country: "",
        zip: "",
    });
    const [setContactDetails, { data: createUpdateContactsDetailsResponse }] =
        useSetContactDetailsMutation();
    const [isFetchingData, setIsFetchingData] = useState(true);

    const userId = useAppSelector((state) => state.user.id);

    const [navigateToNextPage, setNavigateToNextPage] = useState(false);
    const navigateTo = useNavigate();

    const { id } = useAppSelector((state) => state.user);

    const { data: userData } = useGetUserQuery(id ?? "", {
        refetchOnMountOrArgChange: true,
    });

    const [countries, setCountries] = useState<Option[]>([]);

    const [updateProfileData, { isLoading, isSuccess, isError }] =
        useUpdateAgencyProfileDataMutation();
    const { data, refetch } = useGetProfileStrengthQuery(id ?? "", {
        skip: !id,
    });
    const { data: countriesData } = useGetCountriesQuery(
        {},
        {
            refetchOnMountOrArgChange: true,
        }
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        setInputs((prev) => ({
            ...prev,
            [name]: value,
        }));

        setInputErrors((prev) => ({
            ...prev,
            [name]: validations(name, value, true),
        }));
    };

    const handleSelectChange = (name: string, value: string) => {
        setInputs((prev) => ({
            ...prev,
            [name]: value,
        }));

        setInputErrors((prev) => ({
            ...prev,
            [name]: validations(name, value),
        }));
    };

    const handleSubmit = (next: boolean) => {
        const { hasError, errors } = getInputErrors(inputs);

        setInputErrors(errors);

        if (!hasError) {
            setNavigateToNextPage(next);
            setContactDetails({
                code: "+91",
                mobile_number: inputs.phone,
                is_whatsapp_different: false,
                whatsapp_number: inputs.phone,
                whatsapp_code: "+91",
                user_id: userId ?? "",
                method: userData?.data?.attributes?.contact_detail
                    ?.mobile_number
                    ? "PUT"
                    : "POST",
            });
            updateProfileData({
                user_id: userId ?? "",
                data: {
                    first_name: inputs.firstName,
                    last_name: inputs.lastName,
                    phone: inputs.phone,
                    agency: {
                        address: inputs.address,
                        city: inputs.city,
                        zip: inputs.zip,
                        state: inputs.state,
                        country: inputs.country,
                        mobile_number: inputs.phone,
                    },
                },
            }).then(() => {
                refetch();
            });
        }
    };

    useEffect(() => {
        if (isSuccess) {
            addToast({
                type: "success",
                text: "Data saved successfully!",
            });
            if (navigateToNextPage) {
                navigateTo(
                    routeConstants.profile.editProfile.agency.root +
                        routeConstants.profile.editProfile.agency.socialMedia
                );
            }
        }
    }, [isSuccess, navigateTo, navigateToNextPage]);

    useEffect(() => {
        if (isError) {
            addToast({
                type: "error",
                text: "Something went wrong, please try again.",
            });
        }
    }, [isError]);

    useEffect(() => {
        if (countries.length > 0 && userData) {
            setIsFetchingData(false);
        }
    }, [countries.length, userData]);

    useEffect(() => {
        if (countriesData && countriesData.length > 0) {
            setCountries(
                countriesData.map((country) => ({
                    label: country.name,
                    value: country.name,
                }))
            );
        }
    }, [countriesData]);

    useEffect(() => {
        if (userData) {
            setInputs({
                city: userData.data.attributes.agency?.address.city ?? "",
                address: userData.data.attributes.agency?.address.address ?? "",
                country: userData.data.attributes.agency?.address.country ?? "",
                email: userData.data.attributes.email ?? "",
                firstName: userData.data.attributes.first_name ?? "",
                lastName: userData.data.attributes.last_name ?? "",
                state: userData.data.attributes.agency?.address.state ?? "",
                zip: userData.data.attributes.agency?.address.zip ?? "",
                phone:
                    userData.data.attributes.contact_detail?.mobile_number ??
                    "",
            });
        }
    }, [userData]);

    return {
        inputs,
        inputErrors,
        countries,
        isFetchingData,
        handleInputChange,
        handleSubmit,
        handleSelectChange,
        isLoading,
        navigateToNextPage,
    };
};
